
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import { Market, availableMarkets } from "../../markets/marketData";
import { useDatabaseColumns } from "./useDatabaseColumns";
import { useLoadingState } from "./useLoadingState";

export const useMarketFetch = () => {
  const { toast } = useToast();
  const { dbError, setDbError, missingColumns, checkDatabaseColumns } = useDatabaseColumns();
  const { loading, startLoading, stopLoading } = useLoadingState();
  const [markets, setMarkets] = useState<Market[]>([]);
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  // Function to fetch current market preferences
  const fetchCurrentMarkets = async () => {
    try {
      startLoading();
      
      // First check if columns exist
      const columnsExist = await checkDatabaseColumns();
      if (!columnsExist) {
        // If columns don't exist, display error but continue with default values
        setMarkets([
          {
            ...availableMarkets[0],
            isDefault: true,
            isActive: true
          }
        ]);
        stopLoading();
        return;
      }
      
      // Get current user's organization ID
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("Utilisateur non connecté");
      
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('id, default_market, enabled_markets, active_markets')
        .eq('owner_id', userData.user.id)
        .single();
      
      if (orgError) {
        // If error is due to missing columns, try a more basic query
        if (orgError.code === "42703") {
          console.log("Récupération avec colonnes limitées due à des colonnes manquantes");
          const { data: basicOrgData, error: basicOrgError } = await supabase
            .from('organizations')
            .select('id')
            .eq('owner_id', userData.user.id)
            .single();
            
          if (basicOrgError) throw basicOrgError;
          
          setOrganizationId(basicOrgData.id);
          
          // Set default markets since we couldn't get the real ones
          setMarkets([
            {
              ...availableMarkets[0],
              isDefault: true,
              isActive: true
            }
          ]);
          
          stopLoading();
          return;
        }
        
        throw orgError;
      }
      
      setOrganizationId(orgData.id);
      
      // Prepare markets to display
      let marketsToShow: Market[] = [];
      
      if (orgData.enabled_markets && orgData.enabled_markets.length > 0) {
        // If organization already has configured markets
        marketsToShow = availableMarkets
          .filter(market => orgData.enabled_markets.includes(market.country))
          .map(market => ({
            ...market,
            isDefault: market.country === orgData.default_market,
            isActive: orgData.active_markets ? orgData.active_markets.includes(market.country) : true
          }));
      } else {
        // By default, show Algeria as default and active market
        marketsToShow = [
          {
            ...availableMarkets[0],
            isDefault: true,
            isActive: true
          }
        ];
      }
      
      setMarkets(marketsToShow);
    } catch (error) {
      console.error("Erreur lors de la récupération des marchés:", error);
      
      // If error is due to missing columns and dbError is already true
      if (dbError) {
        toast({
          title: "Mode simulation",
          description: "Fonctionnement en mode local uniquement. Les modifications ne seront pas sauvegardées en base de données.",
          variant: "default",
        });
      } else {
        toast({
          title: "Erreur",
          description: "Impossible de récupérer les informations de marchés.",
          variant: "destructive",
        });
      }
      
      // Set Algeria as default and active market in case of error
      setMarkets([
        {
          ...availableMarkets[0],
          isDefault: true,
          isActive: true
        }
      ]);
    } finally {
      stopLoading();
    }
  };

  // Load markets on mount
  useEffect(() => {
    fetchCurrentMarkets();
  }, []);

  return {
    markets,
    setMarkets,
    loading,
    dbError,
    missingColumns,
    organizationId,
    fetchCurrentMarkets
  };
};
